import React from 'react';
import { motion } from 'framer-motion';
import { HiDocumentText, HiCloudArrowUp, HiMagnifyingGlass, HiFunnel } from 'react-icons/hi2';

export default function EmptyState({ canUpload, onUpload }) {
  const handleUploadClick = () => {
    if (onUpload) {
      onUpload();
    } else {
      document.getElementById('upload-input')?.click();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex min-h-[400px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gradient-to-br from-white via-gray-50 to-white px-4 py-16 text-center sm:min-h-[500px] sm:px-8 sm:py-24"
    >
      {/* Animated icon */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1, type: 'spring', stiffness: 200 }}
        className="relative mb-8"
      >
        <motion.div
          animate={{ rotate: [0, 5, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          className="flex h-28 w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 shadow-lg"
        >
          <HiDocumentText className="h-16 w-16 text-gray-400" />
        </motion.div>
        {/* Floating search icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, type: 'spring' }}
          className="absolute -right-2 -top-2 flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 shadow-lg"
        >
          <HiMagnifyingGlass className="h-5 w-5 text-white" />
        </motion.div>
      </motion.div>

      {/* Title */}
      <motion.h3
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-4 text-3xl font-bold text-black"
      >
        No documents found
      </motion.h3>

      {/* Description */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mb-10 max-w-md text-base leading-relaxed text-gray-600"
      >
        {canUpload
          ? 'Get started by uploading your first document or adjust your search filters to find existing ones.'
          : 'Try adjusting your search filters or contact an administrator to upload documents.'}
      </motion.p>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="flex flex-wrap items-center justify-center gap-3"
      >
        {canUpload ? (
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px -10px rgba(59, 130, 246, 0.5)' }}
            whileTap={{ scale: 0.95 }}
            onClick={handleUploadClick}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/30"
          >
            <HiCloudArrowUp className="h-5 w-5" />
            Upload Document
          </motion.button>
        ) : (
          <div className="rounded-xl border border-gray-300 bg-gray-50 px-6 py-3 text-sm font-medium text-gray-600">
            Contact an administrator to upload documents
          </div>
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-6 py-4 text-base font-semibold text-black shadow-sm transition-all hover:bg-gray-50"
        >
          <HiFunnel className="h-5 w-5" />
          Clear Filters
        </motion.button>
      </motion.div>

      {/* Supported formats */}
      {canUpload && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs text-gray-500"
        >
          <span className="font-medium">Supported:</span>
          {['PDF', 'Word', 'Excel', 'Images'].map((format, idx) => (
            <span key={format} className="rounded-full bg-gray-100 px-3 py-1 font-medium">
              {format}
            </span>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
}
